import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { obtenerSlugDeSubdominio } from './subdominio'

const WorkspaceContext = createContext(null)

export function WorkspaceProvider({ usuario, children }) {
  const [workspaces, setWorkspaces] = useState([])
  const [workspaceActivo, setWorkspaceActivo] = useState(null)
  const [cargandoWorkspace, setCargandoWorkspace] = useState(true)
  const [esSuperAdmin, setEsSuperAdmin] = useState(false)
  const [esComercial, setEsComercial] = useState(false)
  const [modoSoporte, setModoSoporte] = useState(false)

  const cargarWorkspaces = useCallback(async () => {
    if (!usuario) {
      setWorkspaces([])
      setWorkspaceActivo(null)
      setEsSuperAdmin(false)
      setEsComercial(false)
      setCargandoWorkspace(false)
      return
    }
    setCargandoWorkspace(true)

    const [{ data: memberships }, { data: perfil }, { data: superAdminRow }, { data: comercialRow }] = await Promise.all([
      supabase.from('workspace_miembros')
        .select('rol, productos(modulos_config), workspaces(id, nombre, slug, logo_url, color_primario, color_secundario, config)')
        .eq('usuario_id', usuario.id)
        .eq('estado', 'activo'),
      supabase.from('usuarios').select('workspace_activo_id').eq('id', usuario.id).single(),
      supabase.from('super_admins').select('usuario_id').eq('usuario_id', usuario.id).maybeSingle(),
      supabase.from('equipo_comercial').select('usuario_id').eq('usuario_id', usuario.id).eq('activo', true).maybeSingle()
    ])
    setEsSuperAdmin(!!superAdminRow)
    setEsComercial(!!comercialRow)

    const propios = (memberships || [])
      .filter(m => m.workspaces)
      .map(m => ({ ...m.workspaces, rolEnWorkspace: m.rol, modulosConfig: m.productos?.modulos_config || null }))
    setWorkspaces(propios)

    // Si se entró por slug.juco.app y el usuario es miembro activo de ESE
    // workspace, tiene prioridad sobre lo que estuviera guardado como
    // activo antes — entrar por el link/subdominio de una nutri puntual
    // gana. Si el usuario NO es miembro de ese workspace, no pasa nada
    // especial: sigue el comportamiento normal de abajo.
    const slugSubdominio = obtenerSlugDeSubdominio()
    let activo = (slugSubdominio && propios.find(w => w.slug === slugSubdominio)) || null

    if (!activo) activo = propios.find(w => w.id === perfil?.workspace_activo_id) || null

    // Si no hay uno guardado como activo pero solo pertenece a uno, se
    // autoasigna. Si pertenece a varios, queda en null hasta que elija
    // (ver el selector en App.js).
    if (!activo && propios.length === 1) {
      activo = propios[0]
    }

    if (activo && activo.id !== perfil?.workspace_activo_id) {
      await supabase.from('usuarios').update({ workspace_activo_id: activo.id }).eq('id', usuario.id)
    }

    setWorkspaceActivo(activo)
    setCargandoWorkspace(false)
  }, [usuario])

  useEffect(() => { cargarWorkspaces() }, [cargarWorkspaces])

  // Theming en runtime: pisa las custom properties de App.css. Si no hay
  // workspace activo todavía, no se toca nada (queda el default de marca).
  useEffect(() => {
    if (!workspaceActivo) return
    document.documentElement.style.setProperty('--verde-dark', workspaceActivo.color_primario || '#5b7fbc')
    document.documentElement.style.setProperty('--verde', workspaceActivo.color_secundario || '#dac2da')
  }, [workspaceActivo])

  const elegirWorkspace = async (workspaceId) => {
    const elegido = workspaces.find(w => w.id === workspaceId)
    if (!elegido || !usuario) return
    setModoSoporte(false)
    await supabase.from('usuarios').update({ workspace_activo_id: workspaceId }).eq('id', usuario.id)
    setWorkspaceActivo(elegido)
  }

  // Botón "Entrar" del panel super-admin (PanelSuperAdmin.js): deja al
  // super-admin operar la configuración/negocio de un workspace ajeno
  // (logo, colores, nombre) sin ser member. A propósito NO persiste
  // usuarios.workspace_activo_id (es una sesión de soporte efímera, no una
  // preferencia real: si se guardara, el próximo login normal de este
  // super-admin no encontraría ese workspace en sus propias memberships y
  // quedaría en un estado confuso) ni suma el workspace al array
  // "workspaces" (ese array son las membresías reales, usadas por el
  // selector de Perfil.js/SelectorWorkspace.js).
  //
  // Si el super-admin YA es owner/staff real de ese workspace puntual, se
  // usa el camino normal (elegirWorkspace) para no pisarle su rol real con
  // rolEnWorkspace=null — modo soporte es solo para workspaces ajenos.
  //
  // rolEnWorkspace queda null a propósito: es lo que hace que
  // bloqueadoClinico (FichaPaciente.js/DatosCompartidosPaciente.js) siga
  // negando ficha/notas/plan/suplementos del paciente, sin excepción,
  // mientras el resto del panel (Configuración, Productos, Planes/Hábitos/
  // Suplementos catálogo) queda disponible vía las policies que ya
  // autorizan is_super_admin() directamente.
  const entrarComoSoporte = async (workspace) => {
    if (!esSuperAdmin) return
    const propio = workspaces.find(w => w.id === workspace.id)
    if (propio) { await elegirWorkspace(workspace.id); return }
    setModoSoporte(true)
    setWorkspaceActivo({
      id: workspace.id,
      nombre: workspace.nombre,
      slug: workspace.slug,
      logo_url: workspace.logo_url,
      color_primario: workspace.color_primario,
      color_secundario: workspace.color_secundario,
      config: workspace.config || {},
      rolEnWorkspace: null
    })
  }

  const salirModoSoporte = () => {
    setModoSoporte(false)
    cargarWorkspaces()
  }

  // Usada por ConfiguracionEspacio.js tras guardar colores/logo. En modo
  // soporte no puede ser un alias de cargarWorkspaces (que solo mira
  // membresías reales) — eso expulsaría al super-admin del workspace ajeno
  // apenas guarda un cambio. Refresca en el lugar la fila del workspace
  // activo en vez de recalcular todo desde cero.
  const recargarWorkspaces = async () => {
    if (modoSoporte && workspaceActivo) {
      const { data } = await supabase.from('workspaces')
        .select('id, nombre, slug, logo_url, color_primario, color_secundario, config')
        .eq('id', workspaceActivo.id)
        .single()
      if (data) setWorkspaceActivo(prev => ({ ...prev, ...data }))
      return
    }
    await cargarWorkspaces()
  }

  // Se llama con el resultado de la RPC redimir_invitacion (workspace_id,
  // nombre, slug, color_primario, color_secundario) justo después de una
  // redención exitosa. Activa el workspace nuevo al toque sin esperar un
  // recargarWorkspaces() completo (evita depender del estado todavía no
  // actualizado en este mismo ciclo de render).
  const unirseWorkspace = async (ws) => {
    if (!usuario) return
    const nuevo = {
      id: ws.workspace_id,
      nombre: ws.nombre,
      slug: ws.slug,
      color_primario: ws.color_primario,
      color_secundario: ws.color_secundario,
      config: {},
      rolEnWorkspace: ws.rol
    }
    setWorkspaces(prev => prev.some(w => w.id === nuevo.id) ? prev : [...prev, nuevo])
    setWorkspaceActivo(nuevo)
    await supabase.from('usuarios').update({ workspace_activo_id: nuevo.id }).eq('id', usuario.id)
  }

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      workspaceActivo,
      workspaceId: workspaceActivo?.id || null,
      cargandoWorkspace,
      esSuperAdmin,
      esComercial,
      modoSoporte,
      elegirWorkspace,
      unirseWorkspace,
      entrarComoSoporte,
      salirModoSoporte,
      recargarWorkspaces
    }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace debe usarse dentro de <WorkspaceProvider>')
  return ctx
}
